// Canonical sizes that all other sizes should map to
// Canonical sizes MUST be present in the master_size_list for a style
const CANONICAL_SIZES = [
  "XS",
  "S",
  "S/M",
  "M",
  "L",
  "L/XL",
  "XL",
  "2XL",
  "3XL",
  "4XL",
  "5XL",
  "6XL",
  "O/S",
  "NB",
  "6M",
  "12M",
  "18M",
  "24M",
  // Toddler sizes
  "2T",
  "3T",
  "4T",
  "5/6T",
];

// Direct mapping of all possible size variations to their canonical form
const COALESCE_SIZES = {
  // Standard sizes map to themselves
  XS: "XS",
  S: "S",
  "S/M": "S/M",
  M: "M",
  L: "L",
  "L/XL": "L/XL",
  XL: "XL",
  "2XL": "2XL",
  "2XL ": "2XL", // Handle trailing space
  "3XL": "3XL",
  "4XL": "4XL",
  "5XL": "5XL",
  "6XL": "6XL",

  // Alternative formats for XL sizes
  XXL: "2XL",
  XX: "2XL",
  XXXL: "3XL",
  "3X": "3XL",
  XXXXL: "4XL",
  "4X": "4XL",
  XXXXXL: "5XL",
  "5X": "5XL",
  XXXXXXL: "6XL",
  "6X": "6XL",

  // Extra small variations
  XXS: "XS",
  "2XS": "XS",

  // Tall variations map to their base size
  "ST": "S",
  "MT": "M",
  "LT": "L",
  "XLT": "XL",
  "2XLT": "2XL",
  XXLT: "2XL",
  "3XLT": "3XL",
  XXXLT: "3XL",
  "4XLT": "4XL",
  "5XLT": "5XL",
  "6XLT": "6XL",

  // Size ranges
  "XS/S": "S",
  "M/L": "L",
  "XL/2XL": "2XL", // Map to 2XL instead of keeping as separate
  "XL/XXL": "2XL", // Map to 2XL

  // One size variations
  "O/S": "O/S",
  "OSFA": "O/S",
  "OSFM": "O/S",
  "ONE SIZE": "O/S",
  "ONE": "O/S",
  "ADJUSTABLE": "O/S",
  "ADJUSTABLE, ONE SIZE FITS MOST": "O/S",

  // Youth sizes
  "YXS": "XS",
  "YS": "S",
  "YM": "M",
  "YL": "L",
  "YXL": "XL",
  "YXS (2/4)": "XS",
  "YS (6/8)": "S",
  "YM (10/12)": "M",
  "YL (14/16)": "L",
  "YXL (18/20)": "XL",
  "YOUTH XS": "XS",
  "YOUTH S": "S",
  "YOUTH M": "M",
  "YOUTH L": "L",
  "YOUTH XL": "XL",
  "YOUTH SMALL": "S",
  "YOUTH MEDIUM": "M",
  "YOUTH LARGE": "L",

  // Newborn size
  NB: "NB",
  NEWBORN: "NB",

  // Month-based sizes (mapping all to M version)
  "6M": "6M",
  "6MO": "6M",
  "6MOS": "6M",
  "12M": "12M",
  "12MO": "12M",
  "12MOS": "12M",
  "18M": "18M",
  "18MO": "18M",
  "18MOS": "18M",
  "24M": "24M",
  "24MO": "24M",
  "24MOS": "24M",

  // Toddler sizes
  "2T": "2T",
  "3T": "3T",
  "4T": "4T",
  "5/6": "5/6T",
  "5/6T": "5/6T",
};

// Function to get the canonical form of a size
export const getCanonicalSize = (size) => {
  if (!size) return null;
  let normalizedSize =
    typeof size === "string"
      ? size.trim().toUpperCase()
      : String(size).trim().toUpperCase();
  // Remove "YTH" from the size
  normalizedSize = normalizedSize.replace("YTH", "").trim();
  return COALESCE_SIZES[normalizedSize] || normalizedSize;
};

// Function to deduplicate sizes by converting all to canonical form
export const deduplicateSizes = (sizeList) => {
  if (!sizeList || !Array.isArray(sizeList) || sizeList.length === 0) {
    return [];
  }

  // Convert all sizes to their canonical form and remove duplicates
  const canonicalSizes = new Set(
    sizeList.map((size) => getCanonicalSize(size)).filter(Boolean), // Remove null/undefined values
  );

  // Convert back to array and sort
  return sortSizes(Array.from(canonicalSizes));
};

// Function to sort sizes in canonical order
export const sortSizes = (sizes) => {
  return [...sizes].sort((a, b) => {
    const indexA = CANONICAL_SIZES.indexOf(a);
    const indexB = CANONICAL_SIZES.indexOf(b);

    // If both sizes are in our canonical list, sort by their order
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If only one size is canonical, prefer it
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    // For non-canonical sizes, sort alphabetically
    return a.localeCompare(b);
  });
};

// For backwards compatibility
export const getSizeAliases = (size) => {
  const canonical = getCanonicalSize(size);
  return new Set([size, canonical]);
};

export default {
  deduplicateSizes,
  sortSizes,
  getSizeAliases,
  getCanonicalSize,
};
